export interface TrackingStep {
  number: number;
  labelKey: string;
  statusKeyword: string;
  completed: boolean;
  date: string | null;
  time: string | null;
}

export interface HistoricoEntry {
  estado: string;
  fechamostrar: string;
  descripcion: string;
  aclaracion: string;
  novedad: number;
}

export const TIMELINE_STEPS = [
  { number: 1, labelKey: 'trackingStep1', statusKeyword: 'statusConfirmado' },
  { number: 2, labelKey: 'trackingStep2', statusKeyword: 'statusRecogido' },
  { number: 3, labelKey: 'trackingStep3', statusKeyword: 'statusEnOrigen' },
  { number: 4, labelKey: 'trackingStep4', statusKeyword: 'statusEnCamino' },
  { number: 5, labelKey: 'trackingStep5', statusKeyword: 'statusEnDestino' },
  { number: 6, labelKey: 'trackingStep6', statusKeyword: 'statusEnReparto' },
  { number: 7, labelKey: 'trackingStep7', statusKeyword: 'statusEntregado' },
];

// Maps Aveonline estado values (case-insensitive) to our timeline step numbers
const STATUS_TO_STEP: Record<string, number> = {
  'confirmado': 1,
  'envio programado': 1,
  'envío programado': 1,
  'programado': 1,
  'recogido': 2,
  'en bodega origen': 3,
  'en origen': 3,
  'en tránsito': 4,
  'en transito': 4,
  'en camino': 4,
  'en bodega destino': 5,
  'en destino': 5,
  'en reparto': 6,
  'entregado': 7,
};

export function mapStatusToCompletedStep(estado: string): number {
  const normalized = estado.trim().toLowerCase();
  if (STATUS_TO_STEP[normalized] !== undefined) {
    return STATUS_TO_STEP[normalized];
  }
  // Partial match fallback for unexpected formats
  for (const [key, step] of Object.entries(STATUS_TO_STEP)) {
    if (normalized.includes(key)) return step;
  }
  return 1;
}

function formatTrackingDate(datePart: string): string {
  const normalizedDate = datePart.split('T')[0].replace(/-/g, '/');
  const datePieces = normalizedDate.split('/');

  if (datePieces.length !== 3) return normalizedDate;

  const [first, second, third] = datePieces;
  const yearFirst = first.length === 4;
  const day = yearFirst ? third : first;
  const month = second;
  const year = yearFirst ? first : third;
  const fullYear = year.length === 2 ? `20${year}` : year;

  return `${day}/${month}/${fullYear}`;
}

/**
 * Parses "DD/MM/YYYY HH:MM" into { date: "DD/MM/YYYY", time: "H:MM AM/PM" }
 */
export function parseDisplayDateTime(fechamostrar: string): { date: string; time: string } {
  const parts = fechamostrar.trim().replace('T', ' ').split(/\s+/);
  const datePart = parts[0] ? formatTrackingDate(parts[0]) : '';
  const timePart = parts[1] || '';

  let formattedTime = '';
  if (timePart) {
    const [hStr, mStr] = timePart.split(':');
    const h = parseInt(hStr, 10);
    const m = mStr || '00';
    if (!isNaN(h)) {
      const period = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      formattedTime = `${h12}:${m} ${period}`;
    }
  }

  return { date: datePart, time: formattedTime };
}

/**
 * Builds the 7-step timeline from the Aveonline historicos array.
 * Each historico entry is matched to a step; the highest matched step
 * determines completedUpTo. Dates come from the historicos.
 */
export function buildTimelineFromHistoricos(historicos: HistoricoEntry[]): TrackingStep[] {
  // Map each historico to a step number and collect dates per step
  const stepDates: Record<number, { date: string; time: string }> = {};
  let completedUpTo = 0;

  for (const entry of historicos) {
    const stepNum = mapStatusToCompletedStep(entry.estado);
    if (stepNum > completedUpTo) {
      completedUpTo = stepNum;
    }
    // Keep the latest entry for each step
    if (!stepDates[stepNum]) {
      stepDates[stepNum] = parseDisplayDateTime(entry.fechamostrar);
    }
  }

  return TIMELINE_STEPS.map((step) => ({
    number: step.number,
    labelKey: step.labelKey,
    statusKeyword: step.statusKeyword,
    completed: step.number <= completedUpTo,
    date: stepDates[step.number]?.date ?? null,
    time: stepDates[step.number]?.time ?? null,
  }));
}

export function buildTimelineFromStatus(status: string, date?: string | null): TrackingStep[] {
  const completedUpTo = mapStatusToCompletedStep(status);
  const parsedDate = date ? parseDisplayDateTime(date) : null;

  return TIMELINE_STEPS.map((step) => ({
    number: step.number,
    labelKey: step.labelKey,
    statusKeyword: step.statusKeyword,
    completed: step.number <= completedUpTo,
    date: step.number === completedUpTo ? parsedDate?.date ?? null : null,
    time: step.number === completedUpTo ? parsedDate?.time ?? null : null,
  }));
}

/**
 * Sentence-case: first letter uppercase, the rest lowercase.
 * Locale-aware so accented characters (Á→á) are handled correctly.
 * "ANULADA" -> "Anulada", "EN TRÁNSITO" -> "En tránsito".
 */
export function toSentenceCase(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return trimmed.charAt(0).toLocaleUpperCase() + trimmed.slice(1).toLocaleLowerCase();
}
